import { toast } from '@/components/ui/use-toast';
import type { ApiError } from '@/lib/api/error';
import { legacyT } from '@/lib/i18n/adapters/i18next';
import { defaultNS } from '@/lib/i18n/config/translations';
import { createZustandContext } from '@/lib/store/zustand-context-pattern';
import type { DeviceDetailSection, DeviceDetailAttributeValue, DeviceSectionFormData } from '../-query/type';
type State = {
  values: DeviceSectionFormData;
  errors: Record<string, string>;
};
type Actions = {
  reset: (data: DeviceSectionFormData) => void;
  validate: (sectionKey?: string) => boolean;
  getUpdateRequest: () => DeviceSectionFormData;
  updateSectionAttribute: (sectionKey: string, displayOrder: number, attributeKey: string, value: DeviceDetailAttributeValue, displayValue?: string | null) => void;
  addSection: (sectionKey: string) => void;
  removeSection: (sectionKey: string, displayOrder: number) => void;
  onApiError: (error: ApiError) => void;
};
export const storeCreator: Parameters<typeof createZustandContext<State & {
  actions: Actions;
}>>[0] = (set, get) => ({
  values: {
    sections: []
  },
  errors: {},
  actions: {
    reset: (data: DeviceSectionFormData) => {
      set({
        values: {
          sections: data.sections ?? []
        },
        errors: {}
      });
    },
    validate: (sectionKey?: string) => {
      const {
        values
      } = get();
      const errors: Record<string, string> = {};
      const targetSections = sectionKey ? values.sections?.filter(s => String(s.sectionKey) === sectionKey) ?? [] : values.sections ?? [];
      for (const section of targetSections) {
        for (const attribute of section.attributes) {
          if (attribute.required) {
            const value = attribute.value;
            if (value === null || value === undefined || typeof value === 'string' && value.trim() === '') {
              const label = attribute.name ?? String(attribute.attributeKey);
              const errorKey = `${String(section.sectionKey)}${section.displayOrder}${String(attribute.attributeKey)}`;
              errors[errorKey] = legacyT('pages.devices.detail.validation.required', {
                field: label,
                ns: defaultNS
              });
            }
          }
        }
      }
      if (Object.keys(errors).length > 0) {
        const errorMessages = Object.values(errors).join('\n');
        toast({
          variant: 'destructive',
          title: errorMessages
        });
        set({
          errors
        });
        return false;
      }
      set({
        errors: {}
      });
      return true;
    },
    getUpdateRequest: (): DeviceSectionFormData => {
      const {
        values
      } = get();
      const sortedSections = [...(values.sections ?? [])].sort((a, b) => a.displayOrder - b.displayOrder);
      const reorderedSections = sortedSections.map((section, index) => ({
        ...section,
        displayOrder: index + 1
      }));
      return structuredClone({
        ...values,
        sections: reorderedSections
      });
    },
    updateSectionAttribute: (sectionKey: string, displayOrder: number, attributeKey: string, value: DeviceDetailAttributeValue, displayValue?: string | null) => {
      set(state => {
        const section = state.values.sections?.find(s => String(s.sectionKey) === sectionKey && s.displayOrder === displayOrder);
        if (section) {
          const attribute = section.attributes.find(attr => String(attr.attributeKey) === attributeKey);
          if (attribute) {
            attribute.value = value;
            attribute.displayValue = displayValue ?? null;
          }
        }
      });
    },
    addSection: (sectionKey: string) => {
      const existingSections = get().values.sections?.filter(s => String(s.sectionKey) === sectionKey) ?? [];
      if (existingSections.length === 0) {
        toast({
          variant: 'destructive',
          title: legacyT('pages.devices.detail.error.sectionNotFound', {
            key: sectionKey,
            ns: defaultNS
          })
        });
        return;
      }
      set(state => {
        const template = existingSections[0];
        const maxDisplayOrder = Math.max(...existingSections.map(s => s.displayOrder), 0);
        const newSection: DeviceDetailSection = structuredClone(template);
        newSection.displayOrder = maxDisplayOrder + 1;
        newSection.sectionInstanceId = null;
        newSection.attributes = newSection.attributes.map(attr => ({
          ...attr,
          value: null
        }));
        state.values.sections.push(newSection);
      });
    },
    removeSection: (sectionKey: string, displayOrder: number) => {
      set(state => {
        const index = state.values.sections.findIndex(s => String(s.sectionKey) === sectionKey && s.displayOrder === displayOrder);
        if (index !== -1) {
          state.values.sections.splice(index, 1);
        }
      });
    },
    onApiError: (error: ApiError) => {
      const overallMessage = error.response?.displayMessage;
      if (overallMessage) {
        toast({
          variant: 'destructive',
          title: overallMessage
        });
        return;
      }
      if (error.response?.validationMessages) {
        const state = get();
        const errorMessages = Object.entries(error.response.validationMessages).map(([key, messages]) => {
          let displayName = key;
          for (const section of state.values.sections ?? []) {
            const attribute = section.attributes.find(attr => String(attr.attributeKey) === key);
            if (attribute?.name) {
              displayName = String(attribute.name);
              break;
            }
          }
          return `${displayName}: ${messages.join(', ')}`;
        }).join('\n');
        toast({
          variant: 'destructive',
          title: errorMessages
        });
      }
    }
  }
});
export const {
  context: DeviceDetailFormStore,
  useStore: useDeviceDetailFormStore
} = createZustandContext<State & {
  actions: Actions;
}>(storeCreator);